import { useEffect, type FC, type ReactNode } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Compass, Home, LogIn } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useAuth } from '../auth/AuthContext';
import { logClient404Hit, type Client404Kind } from '../api/client404Api';
import { buildCollectionPath } from '../utils/collectionPaths';

export type NotFoundPageProps = {
  kind: Client404Kind;
  /** Overrides pathname + search for server logging */
  pathOverride?: string;
  adminActions?: ReactNode;
  availableSlugs?: string[];
  title?: string;
  /** When true, use transparent background (already inside a framed layout) */
  embedded?: boolean;
};

function kindMessage(kind: Client404Kind): string {
  switch (kind) {
    case 'collection_not_found':
      return 'This collection does not exist yet, or the link may be outdated.';
    case 'component_not_found':
      return 'That collection is not available for your account, or it no longer exists.';
    default:
      return 'That page is not part of this app. Double-check the URL or use the menu to navigate.';
  }
}

const NotFoundPage: FC<NotFoundPageProps> = ({
  kind,
  pathOverride,
  adminActions,
  availableSlugs,
  title,
  embedded,
}) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { authenticated } = useAuth();
  const fullPath = pathOverride ?? `${location.pathname}${location.search || ''}`;

  useEffect(() => {
    const referrer =
      typeof document !== 'undefined' && document.referrer
        ? document.referrer
        : undefined;
    logClient404Hit({
      path: fullPath.startsWith('/') ? fullPath : `/${fullPath}`,
      kind,
      referrer,
    });
  }, [fullPath, kind]);

  const shellClass = embedded
    ? 'relative overflow-hidden min-h-[70vh] flex flex-col items-center justify-center px-4 py-12 rounded-2xl border border-red-200/80'
    : 'relative overflow-hidden min-h-screen flex flex-col items-center justify-center px-4 py-16';

  return (
    <div className={shellClass}>
      <div
        className="pointer-events-none absolute inset-0 bg-[#f6f4f2]"
        aria-hidden
      />
      <div
        className="pointer-events-none absolute inset-0 opacity-95"
        aria-hidden
        style={{
          background: `
            radial-gradient(ellipse 120% 80% at 50% -20%, rgba(254, 226, 226, 0.9), transparent 55%),
            radial-gradient(ellipse 90% 70% at 100% 100%, rgba(254, 215, 215, 0.85), transparent 50%),
            radial-gradient(ellipse 70% 50% at 0% 80%, rgba(219, 234, 254, 0.55), transparent 45%)
          `,
        }}
      />
      <div
        className="pointer-events-none absolute inset-0 mix-blend-multiply opacity-[0.08]"
        aria-hidden
        style={{
          backgroundImage: `repeating-linear-gradient(
            0deg,
            transparent,
            transparent 2px,
            rgba(0,0,0,0.04) 2px,
            rgba(0,0,0,0.04) 4px
          )`,
        }}
      />
      <div
        className="pointer-events-none absolute inset-0 opacity-[0.4]"
        aria-hidden
        style={{
          backgroundImage: `linear-gradient(rgba(0, 0, 0, 0.04) 1px, transparent 1px),
            linear-gradient(90deg, rgba(0, 0, 0, 0.04) 1px, transparent 1px)`,
          backgroundSize: '64px 64px',
          maskImage: 'radial-gradient(ellipse 70% 60% at 50% 45%, black 10%, transparent 75%)',
          WebkitMaskImage:
            'radial-gradient(ellipse 70% 60% at 50% 45%, black 10%, transparent 75%)',
        }}
      />
      <div
        className="pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_at_center,transparent_35%,rgba(15,23,42,0.06)_100%)]"
        aria-hidden
      />

      <div className="relative z-[1] w-full max-w-lg text-center space-y-8">
        <div className="space-y-4">
          <h1 className="text-8xl sm:text-9xl font-black tracking-tighter leading-none text-transparent bg-clip-text bg-gradient-to-b from-neutral-900 via-neutral-700 to-neutral-500">
            404
          </h1>
          <h2 className="text-lg sm:text-xl font-light tracking-wide text-neutral-900">
            {title ?? 'Page not found'}
          </h2>
          <p className="text-sm sm:text-base text-neutral-600 leading-relaxed max-w-md mx-auto">
            {kindMessage(kind)}
          </p>
          <p className="text-[11px] sm:text-xs font-mono text-neutral-700 break-all rounded-sm border border-red-200/90 bg-white/70 px-3 py-2.5">
            {fullPath}
          </p>
        </div>

        {availableSlugs && availableSlugs.length > 0 ? (
          <div className="rounded-lg border border-red-200/80 bg-white/65 p-4 text-left backdrop-blur-sm">
            <p className="text-[10px] font-semibold uppercase tracking-widest text-neutral-600 mb-3">
              Collections you can open
            </p>
            <ul className="flex flex-wrap gap-2">
              {availableSlugs.map((slug) => {
                const s = typeof slug === 'string' ? slug.replace(/[!]+$/, '').trim() : slug;
                if (!s) return null;
                return (
                  <li key={s}>
                    <Link
                      to={buildCollectionPath({ slug: s })}
                      className="inline-flex items-center rounded border border-red-300/90 bg-red-50/90 px-2.5 py-1 text-xs font-medium text-red-900 hover:bg-red-100 hover:border-red-400/80 transition-colors"
                    >
                      {s}
                    </Link>
                  </li>
                );
              })}
            </ul>
          </div>
        ) : null}

        {adminActions ? (
          <div className="flex flex-wrap items-center justify-center gap-3">{adminActions}</div>
        ) : null}

        <div className="flex flex-wrap items-center justify-center gap-3 pt-2">
          <Button
            type="button"
            variant="secondary"
            className="gap-2 border border-neutral-300 bg-white text-neutral-900 hover:bg-neutral-50"
            onClick={() => navigate(-1)}
          >
            <Compass className="h-4 w-4" />
            Back
          </Button>
          <Button
            type="button"
            className="gap-2 border border-red-700 bg-red-600 text-white hover:bg-red-700"
            onClick={() => navigate('/')}
          >
            <Home className="h-4 w-4" />
            Home
          </Button>
          {!authenticated ? (
            <Button
              type="button"
              variant="outline"
              className="gap-2 border-neutral-400 bg-transparent text-neutral-800 hover:bg-neutral-100"
              onClick={() => navigate('/login')}
            >
              <LogIn className="h-4 w-4" />
              Log in
            </Button>
          ) : null}
        </div>
      </div>
    </div>
  );
};

export default NotFoundPage;
